import { useState, useEffect } from 'react';

/**
 * Хук для «устранения дребезга» значений. Позволяет откладывать обновление состояния на указанную задержку.
 * Полезен в поисковых полях и других сценариях, где нужно уменьшить количество запросов при быстром вводе.
 *
 * @param value Значение, которое нужно стабилизировать
 * @param delay Задержка в миллисекундах (по умолчанию 300)
 * @returns Значение, обновлённое после истечения задержки
 *
 * @example
 * ```tsx
 * const [searchTerm, setSearchTerm] = useState('');
 * const debouncedSearchTerm = useDebounce(searchTerm, 500);
 *
 * useEffect(() => {
 *   if (debouncedSearchTerm) {
 *     performSearch(debouncedSearchTerm);
 *   }
 * }, [debouncedSearchTerm]);
 * ```
 */
export function useDebounce<T>(value: T, delay: number = 300): T {
  const [debouncedValue, setDebouncedValue] = useState<T>(value);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => clearTimeout(timer);
  }, [value, delay]);

  return debouncedValue;
}

export default useDebounce;
